import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { MiniQuestionnaireData } from "@/types/miniQuestionnaire";

interface MiniSection3Props {
  data: MiniQuestionnaireData;
  updateData: (field: keyof MiniQuestionnaireData, value: string) => void;
}

const MiniSection3 = ({ data, updateData }: MiniSection3Props) => {
  const calculatePaquetAnnees = () => {
    const annees = parseInt(data.annees_tabagisme) || 0;
    const cigarettes = parseInt(data.cigarettes_par_jour) || 0;
    return ((cigarettes / 20) * annees).toFixed(1);
  };

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-semibold text-feel-foreground mb-6">
        Section III – Tabac
      </h2>

      {/* Question 5 - Fumez-vous actuellement */}
      <div className="space-y-3">
        <Label className="text-lg font-medium text-feel-foreground">
          Q5. Fumez‑vous actuellement ?
        </Label>
        <RadioGroup
          value={data.fume_actuellement}
          onValueChange={(value) => {
            updateData("fume_actuellement", value);
            if (value === "non") {
              updateData("annees_tabagisme", "");
              updateData("cigarettes_par_jour", "");
            } else {
              updateData("exposition_tabagisme_passif", "");
            }
          }}
          className="space-y-2"
        >
          <div className="flex items-center space-x-3">
            <RadioGroupItem value="non" id="fume-non" />
            <Label htmlFor="fume-non" className="cursor-pointer">Non, je ne fume pas (ou plus)</Label>
          </div>
          <div className="flex items-center space-x-3">
            <RadioGroupItem value="oui" id="fume-oui" />
            <Label htmlFor="fume-oui" className="cursor-pointer">Oui je fume</Label>
          </div>
        </RadioGroup>
      </div>

      {/* Question 6 - Paquet-années (si fumeur) */}
      {data.fume_actuellement === "oui" && (
        <div className="space-y-4 p-4 bg-feel-muted/30 rounded-lg">
          <Label className="text-lg font-medium text-feel-foreground">
            Q6. Depuis combien d'années fumez-vous, et combien de cigarettes fumez-vous par jour ?
          </Label>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="annees_tabagisme">Nombre d'années de tabagisme</Label>
              <Input
                id="annees_tabagisme"
                type="number"
                placeholder="Ex: 10"
                value={data.annees_tabagisme}
                onChange={(e) => updateData("annees_tabagisme", e.target.value)}
                min="0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cigarettes_par_jour">Cigarettes par jour</Label>
              <Input
                id="cigarettes_par_jour"
                type="number"
                placeholder="Ex: 15"
                value={data.cigarettes_par_jour}
                onChange={(e) => updateData("cigarettes_par_jour", e.target.value)}
                min="0"
              />
            </div>
          </div>

          {data.annees_tabagisme && data.cigarettes_par_jour && (
            <div className="mt-4 p-3 bg-feel-primary/10 rounded-lg">
              <p className="text-feel-foreground font-medium">
                Paquet-années : <span className="text-feel-primary">{calculatePaquetAnnees()}</span>
              </p>
            </div>
          )}
        </div>
      )}

      {/* Question 7 - Exposition tabagisme passif (si non-fumeur) */}
      {data.fume_actuellement === "non" && (
        <div className="space-y-3">
          <Label className="text-lg font-medium text-feel-foreground">
            Q7. Bien que vous ne fumiez pas, êtes‑vous régulièrement exposé(e) à la fumée de tabac (au travail, en public, etc.) ?
          </Label>
          <RadioGroup
            value={data.exposition_tabagisme_passif}
            onValueChange={(value) => updateData("exposition_tabagisme_passif", value)}
            className="space-y-2"
          >
            <div className="flex items-center space-x-3">
              <RadioGroupItem value="oui" id="passif-oui" />
              <Label htmlFor="passif-oui" className="cursor-pointer">Oui</Label>
            </div>
            <div className="flex items-center space-x-3">
              <RadioGroupItem value="non" id="passif-non" />
              <Label htmlFor="passif-non" className="cursor-pointer">Non</Label>
            </div>
          </RadioGroup>
        </div>
      )}
    </div>
  );
};

export default MiniSection3;
